
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { AlertCircle, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ProfileCompletionResult } from '@/utils/profileValidation';

interface ProfileCompletionPromptProps {
  completionResult: ProfileCompletionResult;
  action: 'listing' | 'contact';
  onClose?: () => void;
}

const ProfileCompletionPrompt = ({ completionResult, action, onClose }: ProfileCompletionPromptProps) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const handleGoToAccount = () => {
    navigate('/account');
    if (onClose) onClose();
  };

  const actionText = action === 'listing' 
    ? t('list_property', 'إدراج عقار', 'list a property')
    : t('contact_owner', 'التواصل مع المالك', 'contact the owner');

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
          <CardTitle className="text-xl">
            {t('complete_profile_required', 'إكمال الملف الشخصي مطلوب', 'Complete Profile Required')}
          </CardTitle>
          <CardDescription>
            {t('complete_profile_to_action', `يجب إكمال ملفك الشخصي قبل ${actionText}`, `You must complete your profile before you can ${actionText}`)}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-center gap-2 text-yellow-800 mb-2">
              <User className="h-4 w-4" />
              <span className="font-medium">
                {t('missing_information', 'المعلومات المطلوبة', 'Missing Information')}
              </span>
            </div>
            <ul className="text-sm text-yellow-700 space-y-1">
              {completionResult.missingFieldsTranslated[language].map((field, index) => (
                <li key={index}>• {field}</li>
              ))}
            </ul>
          </div>

          <div className="space-y-3">
            <Button onClick={handleGoToAccount} className="w-full">
              {t('complete_profile', 'إكمال الملف الشخصي', 'Complete Profile')}
            </Button>
            
            {onClose && (
              <Button variant="outline" onClick={onClose} className="w-full">
                {t('cancel', 'إلغاء', 'Cancel')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileCompletionPrompt;
